import { useCallback, useEffect, useRef, useState } from "react";
import { realtime, type RealtimeStatus } from "./realtime-client";
import type { RealtimeEventName, RealtimeFrame } from "./realtime-events";

/**
 * Subscribes one component to one realtime event for as long as it is mounted.
 *
 * The handler is held in a ref and the subscription is keyed on the EVENT alone, so a caller
 * may pass an inline arrow function — the usual React trap where every render resubscribes,
 * and on a socket that means unbinding and rebinding on every keystroke.
 *
 * HARD RULE for the handler: react to the frame, do not trust it. Invalidate or refetch the
 * query that owns the data. A frame is a nudge that something changed, not the change itself
 * (`realtime-events.ts`).
 */
export function useRealtimeEvent<T = Record<string, unknown>>(
  event: RealtimeEventName,
  handler: (frame: RealtimeFrame<T>) => void,
): void {
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(
    () => realtime.on(event, (frame) => ref.current(frame as RealtimeFrame<T>)),
    [event],
  );
}

/**
 * The stream's connection state, for the places that should show it.
 *
 * Deliberately NOT a banner the moment a socket drops: a reconnect that takes two seconds is
 * invisible to the person, and announcing it teaches them to ignore the indicator. `live` is
 * true while this browser has a stream — held by this tab (`open`) or by another one
 * (`follower`), which is the same fact for the person looking at the screen.
 */
export function useRealtimeStatus(): RealtimeStatus {
  const [status, setStatus] = useState<RealtimeStatus>(() => realtime.currentStatus());
  useEffect(() => realtime.onStatus(setStatus), []);
  return status;
}

/** How long the stream may be away before it is worth telling anybody. */
export const DISCONNECTED_GRACE_MS = 8_000;

export interface RealtimeConnection {
  status: RealtimeStatus;
  /** This browser is receiving frames (through this tab or another). */
  live: boolean;
  /** Away long enough that the screen should say so. */
  degraded: boolean;
  /** Try again now, whatever the backoff was counting to. */
  reconnect: () => void;
}

export function useRealtimeConnection(): RealtimeConnection {
  const status = useRealtimeStatus();
  const live = status === "open" || status === "follower";
  const [degraded, setDegraded] = useState(false);

  useEffect(() => {
    if (live || status === "idle") {
      setDegraded(false);
      return;
    }
    // `stopped` is already a decision, not a hiccup: the client gave up, so say so at once.
    if (status === "stopped") {
      setDegraded(true);
      return;
    }
    const timer = window.setTimeout(() => setDegraded(true), DISCONNECTED_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [live, status]);

  const reconnect = useCallback(() => realtime.reconnect(), []);
  return { status, live, degraded, reconnect };
}
